import { OrgType } from '@nova/core';

/** Org types whose sessions may be recorded. Test orgs are ours; never record them. */
export const RECORDABLE_ORG_TYPES: readonly string[] = [OrgType.Client, OrgType.Trial];

/** mixpanel-browser caps record_min_ms here (MAX_VALUE_FOR_MIN_RECORDING_MS). */
export const SESSION_REPLAY_MAX_MIN_MS = 8000;

/**
 * Schema version of the object the gate persists under its storage key.
 *
 * It is the sole compatibility mechanism for that object: readStoredState discards
 * any entry whose version differs, so bumping this number invalidates every stored
 * decision and the next evaluation draws a fresh roll. Bump it whenever the shape
 * of StoredGateState changes — never reuse a version for a different shape, and
 * do not encode a version in the storage key as well.
 *
 * It has no effect on SDK initialization; it only guards the shape of what we read
 * back out of sessionStorage.
 */
export const GATE_STATE_VERSION = 1;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SessionReplayInitOptions {
  record_sessions_percent: 0;
  record_max_ms: number;
  record_idle_timeout_ms: number;
  record_min_ms: number;
  record_console: false;
  record_network: false;
}

/**
 * Minimal interface that the gate needs from the analytics client.
 *
 * The implementation must resolve to whether the recording actually started, and
 * must not reject — MixpanelPluginV2.startSessionRecording swallows SDK errors and
 * resolves false. The gate treats a falsy result exactly like a failure.
 */
export interface SessionReplayRecorder {
  startSessionRecording(): boolean | Promise<boolean>;
}

export interface SessionReplayGateContext {
  path: string;
  orgType: string | null;
}

export type GateEvaluationReason =
  | 'already-started'
  | 'disabled'
  | 'route'
  | 'no-user'
  | 'org-type'
  | 'eligible'
  | 'not-sampled'
  | 'start-cap';

interface StoredGateState {
  /** Schema version; always GATE_STATE_VERSION. See the constant for why it exists. */
  v: number;
  roll: number;
  startCount: number;
  firstStartedAt: number | null;
}

export interface GateState {
  roll: number | null;
  startCount: number;
  firstStartedAt: number | null;
  startedInThisPageLoad: boolean;
  lastReason: GateEvaluationReason | null;
}

export interface SessionReplayGate {
  evaluate(context: SessionReplayGateContext): GateEvaluationReason;
  markStarted(): void;
  getState(): GateState;
  clear(): void;
}

export interface SessionReplayGateOptions {
  storageKey: string;
  enabled: () => boolean;
  samplePercent: () => number;
  isEligiblePath: (path: string) => boolean;
  /** Injected storage for tests; defaults to sessionStorage with in-memory fallback. */
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** Injected RNG (0–100) for tests; defaults to Math.random() * 100. */
  random?: () => number;
  /** Maximum number of SDK start calls per session (per tab). Defaults to 3. */
  maxStartsPerSession?: number;
  debug?: boolean;
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function buildStorageWithFallback(
  injected?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
): Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> {
  if (injected) {
    return injected;
  }

  try {
    // Probe for Safari private mode which throws on sessionStorage access
    sessionStorage.setItem('__od_sr_probe', '1');
    sessionStorage.removeItem('__od_sr_probe');
    return sessionStorage;
  } catch {
    // Fall back to an in-memory map; degraded mode redraws the roll per page load
    const map = new Map<string, string>();
    return {
      getItem: (key: string) => map.get(key) ?? null,
      setItem: (key: string, value: string) => {
        map.set(key, value);
      },
      removeItem: (key: string) => {
        map.delete(key);
      }
    };
  }
}

/** A non-negative finite number — the only shape roll and startCount may legally take. */
function isNonNegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

/**
 * Reads the persisted decision, or null when there is nothing usable to read.
 *
 * Every field is validated, not just the version. A partial object — `{"v":1}`, a
 * hand-edited key during QA, a truncated write — would otherwise reach evaluate()
 * with `roll` and `startCount` undefined, where `undefined >= samplePercent()` and
 * `undefined >= maxStartsPerSession` are both false: the session would be treated as
 * eligible at any sample rate and with no start cap. That is the one way this module
 * could fail OPEN, so malformed state is rejected here and the roll is redrawn.
 *
 * Negative numbers are rejected for the same reason (a negative roll always samples
 * in, a negative startCount defeats the cap). A roll above the sample percent simply
 * doesn't sample, which is already the safe direction.
 */
function readStoredState(storage: Pick<Storage, 'getItem'>, key: string): StoredGateState | null {
  try {
    const raw = storage.getItem(key);
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    const candidate = parsed as Record<string, unknown>;
    if (
      candidate.v !== GATE_STATE_VERSION ||
      !isNonNegativeNumber(candidate.roll) ||
      !isNonNegativeNumber(candidate.startCount)
    ) {
      return null;
    }
    return {
      v: GATE_STATE_VERSION,
      roll: candidate.roll,
      startCount: candidate.startCount,
      // Optional on purpose: the documented QA lever seeds { v, roll, startCount } only.
      firstStartedAt: isNonNegativeNumber(candidate.firstStartedAt)
        ? candidate.firstStartedAt
        : null
    };
  } catch {
    return null;
  }
}

function writeStoredState(
  storage: Pick<Storage, 'setItem'>,
  key: string,
  state: StoredGateState
): void {
  try {
    storage.setItem(key, JSON.stringify(state));
  } catch {
    // Storage full or restricted — degrade silently
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns the mixpanel-browser init options for Session Replay.
 *
 * record_sessions_percent is always 0 — sampling is owned by the gate, never
 * delegated to the SDK. record_console is explicitly false because these apps
 * log axios error objects that may carry carrier/driver payloads and JWTs.
 */
export function buildSessionReplayInitOptions({
  recordMaxMs,
  recordIdleTimeoutMs
}: {
  recordMaxMs: number;
  recordIdleTimeoutMs: number;
}): SessionReplayInitOptions {
  return {
    record_sessions_percent: 0,
    record_max_ms: recordMaxMs,
    record_idle_timeout_ms: recordIdleTimeoutMs,
    record_min_ms: SESSION_REPLAY_MAX_MIN_MS,
    record_console: false,
    record_network: false
  };
}

/**
 * Returns a function that tests whether a given path falls under `prefix`.
 *
 * A path matches if it equals the prefix exactly, or starts with prefix + '/'.
 * Query strings and hash fragments are stripped before comparison so
 * /yard?x=1 and /yard#a both match /yard, while /insights/yard and /yardstick
 * do not.
 */
export function createPathPrefixMatcher(prefix: string): (path: string) => boolean {
  const normalizedPrefix = prefix.replace(/\/+$/u, '');
  return (rawPath: string): boolean => {
    // Strip query string and hash, then trailing slashes
    const path = (rawPath.split(/[?#]/u)[0] ?? '').replace(/\/+$/u, '') || '/';
    return path === normalizedPrefix || path.startsWith(`${normalizedPrefix}/`);
  };
}

/**
 * Creates a gate that controls whether a Mixpanel Session Replay should start.
 *
 * Design invariants:
 * - The dice is drawn at most once per tab (persisted in sessionStorage).
 * - Ineligible routes / missing user leave no trace so the next evaluation is fair.
 * - Failing closed everywhere: every error mode records less, never more.
 */
export function createSessionReplayGate(options: SessionReplayGateOptions): SessionReplayGate {
  const {
    storageKey,
    enabled,
    samplePercent,
    isEligiblePath,
    random = () => Math.random() * 100,
    maxStartsPerSession = 3
  } = options;

  const storage = buildStorageWithFallback(options.storage);
  let startedInThisPageLoad = false;
  let lastReason: GateEvaluationReason | null = null;
  /**
   * The roll the last evaluation used, mirrored in memory. If storage is cleared or a
   * write fails between evaluate() and markStarted(), this keeps the persisted roll
   * consistent with the decision that actually permitted the start instead of drawing
   * an unrelated one, which would make getState().roll misleading during QA.
   */
  let lastEvaluatedRoll: number | null = null;

  function evaluate(context: SessionReplayGateContext): GateEvaluationReason {
    if (startedInThisPageLoad) {
      lastReason = 'already-started';
      return 'already-started';
    }

    if (!enabled()) {
      lastReason = 'disabled';
      return 'disabled';
    }

    // Steps 3–5 leave no storage trace so a later navigation gets a fair evaluation
    if (!isEligiblePath(context.path)) {
      lastReason = 'route';
      return 'route';
    }

    if (!context.orgType) {
      lastReason = 'no-user';
      return 'no-user';
    }

    if (!RECORDABLE_ORG_TYPES.includes(context.orgType)) {
      lastReason = 'org-type';
      return 'org-type';
    }

    // Read an existing roll or draw a new one. The roll is persisted, not the decision,
    // so changing samplePercent re-evaluates from the same roll without redrawing.
    let stored = readStoredState(storage, storageKey);
    if (!stored) {
      stored = {
        v: GATE_STATE_VERSION,
        roll: lastEvaluatedRoll ?? random(),
        startCount: 0,
        firstStartedAt: null
      };
      writeStoredState(storage, storageKey, stored);
    }
    lastEvaluatedRoll = stored.roll;

    if (stored.roll >= samplePercent()) {
      lastReason = 'not-sampled';
      return 'not-sampled';
    }

    if (stored.startCount >= maxStartsPerSession) {
      lastReason = 'start-cap';
      return 'start-cap';
    }

    lastReason = 'eligible';
    return 'eligible';
  }

  function markStarted(): void {
    startedInThisPageLoad = true;
    const stored = readStoredState(storage, storageKey);
    const now = Date.now();
    writeStoredState(storage, storageKey, {
      v: GATE_STATE_VERSION,
      roll: stored?.roll ?? lastEvaluatedRoll ?? random(),
      startCount: (stored?.startCount ?? 0) + 1,
      firstStartedAt: stored?.firstStartedAt ?? now
    });
    lastReason = 'already-started';
  }

  function getState(): GateState {
    const stored = readStoredState(storage, storageKey);
    return {
      roll: stored?.roll ?? null,
      startCount: stored?.startCount ?? 0,
      firstStartedAt: stored?.firstStartedAt ?? null,
      startedInThisPageLoad,
      lastReason
    };
  }

  function clear(): void {
    startedInThisPageLoad = false;
    lastReason = null;
    lastEvaluatedRoll = null;
    try {
      storage.removeItem(storageKey);
    } catch {
      // Degrade silently
    }
  }

  return { evaluate, markStarted, getState, clear };
}

// ---------------------------------------------------------------------------
// Orchestration helpers
// ---------------------------------------------------------------------------

export interface AttemptSessionReplayStartOptions {
  gate: SessionReplayGate;
  recorder: SessionReplayRecorder;
  context: SessionReplayGateContext;
}

/**
 * Gates with a start already awaiting the recorder.
 *
 * The gate only learns it started inside markStarted(), which runs *after* the await.
 * Callers are router hooks that don't await this function, so two navigations landing
 * inside that window would both evaluate as eligible and both call markStarted(),
 * burning start-cap slots. (The SDK itself resumes rather than duplicates, so this
 * inflates startCount rather than quota — but the cap is the backstop against a
 * reload loop, so it has to stay honest.)
 *
 * Keyed on the gate object so each app's gate is independent, and weak so a discarded
 * gate is collectable. Kept here rather than on SessionReplayGate to leave that a plain
 * injectable value.
 */
const gatesWithStartInFlight = new WeakSet<SessionReplayGate>();

/**
 * Evaluates the gate and, if eligible, starts the replay. Safe to call on every
 * navigation — short-circuits immediately for non-eligible outcomes. Never throws.
 *
 * markStarted() is only called once the recorder confirms the start, so a transient
 * SDK error — whether it surfaces as a rejection or as a falsy result — leaves
 * startCount unchanged and the next navigation retries.
 */
export async function attemptSessionReplayStart({
  gate,
  recorder,
  context
}: AttemptSessionReplayStartOptions): Promise<void> {
  if (gatesWithStartInFlight.has(gate)) {
    return;
  }

  const reason = gate.evaluate(context);
  if (reason !== 'eligible') {
    return;
  }

  gatesWithStartInFlight.add(gate);
  try {
    const started = await recorder.startSessionRecording();
    if (started) {
      gate.markStarted();
    }
  } catch {
    // Recorder threw — do not mark started; the next navigation will retry
  } finally {
    gatesWithStartInFlight.delete(gate);
  }
}

// ---------------------------------------------------------------------------
// Debug handle
// ---------------------------------------------------------------------------

export interface SessionReplayDebugHandle {
  state: () => GateState;
  replayId: () => string | null;
  replayUrl: () => string | null;
  forceStart?: () => Promise<void>;
}

type RecorderWithReplayAccessors = SessionReplayRecorder & {
  getSessionReplayId?: () => string | null;
  getSessionReplayUrl?: () => string | null;
};

/**
 * Exposes a read-only debug handle on `window[name]`.
 *
 * Not optional polish — `mixpanel-browser` imported as a module does NOT set
 * `window.mixpanel`, so without this handle there is no way to inspect gate
 * state or confirm the replay id during staging QA.
 *
 * forceStart is only included on non-production builds (allowForceStart: true).
 */
export function exposeSessionReplayDebugHandle(
  name: string,
  {
    gate,
    recorder,
    allowForceStart = false
  }: {
    gate: SessionReplayGate;
    recorder: RecorderWithReplayAccessors;
    allowForceStart?: boolean;
  }
): void {
  const handle: SessionReplayDebugHandle = {
    state: () => gate.getState(),
    replayId: () =>
      typeof recorder.getSessionReplayId === 'function' ? recorder.getSessionReplayId() : null,
    replayUrl: () =>
      typeof recorder.getSessionReplayUrl === 'function' ? recorder.getSessionReplayUrl() : null
  };

  if (allowForceStart) {
    // Mirrors attemptSessionReplayStart: only mark the gate once the recorder has
    // actually confirmed the start, so state() never reports a start that did not happen.
    handle.forceStart = async () => {
      try {
        const started = await recorder.startSessionRecording();
        if (!started) {
          console.error('[SessionReplay] forceStart: the recorder declined to start');
          return;
        }
        gate.markStarted();
      } catch (error) {
        console.error('[SessionReplay] forceStart failed', error);
      }
    };
  }

  try {
    Object.defineProperty(window, name, {
      value: handle,
      configurable: true,
      writable: false
    });
  } catch {
    // SSR or restricted environment — skip
  }
}
